import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import { ElementAddress, MediaElement } from 'editor/src/store/design/types';
import getContentAreaFromSpread from 'editor/src/store/design/util/getContentAreaFromSpread';
import addSelectedMediaElementOperation from 'editor/src/store/editor/operation/addSelectedMediaElementOperation';
import getFocusedContent from 'editor/src/store/editor/selector/getFocusedContent';
import showSheetLengthLimitPopupOperation from 'editor/src/store/gangSheet/operation/showSheetLengthLimitPopupOperation';
import getResolvedGapMm from 'editor/src/store/gangSheet/selector/getResolvedGapMm';
import getRollLength from 'editor/src/store/gangSheet/selector/getRollLength';
import findSpotOnRollingSheet from 'editor/src/store/gangSheet/util/findSpotOnRollingSheet';
import { getElementFootprint } from 'editor/src/store/gangSheet/util/getSheetArtworkRects';
import type { Thunk } from 'editor/src/store/hooks';

import { elementUuids } from 'editor/src/util/uuids';

import addMediaElementOperation from './addMediaElementOperation';

const copyMediaElementOperation =
  (
    elementAddress: ElementAddress,
    destinationAddress?: { spreadIndex: number; pageIndex?: number },
    focus = true,
  ): Thunk =>
  (dispatch, getState, { i18n }) => {
    const state = getState();
    const element = getMediaElement(state, elementAddress);

    if (!element) {
      return undefined;
    }

    const uuid = elementUuids.generate();
    const newElement: MediaElement = {
      ...element,
      uuid,
      name: i18n.t('editor-copy-of', { name: element.name }),
    };

    // A copy must not silently join the original's shared-value group. It would
    // be a full member as far as propagation is concerned — the buyer's name
    // printing on it — while never becoming a personalisation field, because the
    // copy is not added to the spread's condition group. So it would take the
    // value with no panel, no entry in the target list and no mention in "Also
    // shared with": visible only on the print file, and removable only by
    // deleting the layer.
    if (newElement.type === 'text' && newElement.extra.sharedFieldId) {
      newElement.extra = { ...newElement.extra, sharedFieldId: undefined };
    }
    // Nor the original's product-layout frame: tagged, the copy would hide with the slot, be
    // swept into every other slot on the next apply and vanish when the slot is replaced. A
    // copy left inside the template frame is adopted again by the next apply.
    delete newElement.slotFrame;

    const destinationSpreadIndex = destinationAddress?.spreadIndex ?? elementAddress.spreadIndex;
    const destinationPageIndex = destinationAddress?.pageIndex ?? elementAddress.pageIndex;
    const focusedContent = getFocusedContent(state);
    const elementContentArea = getContentAreaFromSpread(
      state.design.designData?.spreads[elementAddress.spreadIndex],
      focusedContent,
    );
    const destinationContentArea = getContentAreaFromSpread(
      state.design.designData?.spreads[destinationSpreadIndex],
      focusedContent,
    );

    if (!elementContentArea || !destinationContentArea) {
      return undefined;
    }

    let positioningType: 'shift' | 'fit' | 'same';
    if (destinationAddress && destinationSpreadIndex !== elementAddress.spreadIndex) {
      const areEqualAreas =
        elementContentArea.width === destinationContentArea.width &&
        elementContentArea.height === destinationContentArea.height &&
        elementContentArea.left === destinationContentArea.left &&
        elementContentArea.top === destinationContentArea.top;

      positioningType = areEqualAreas ? 'same' : 'fit';
    } else {
      positioningType = 'shift';
    }

    const rollLength = getRollLength(state);
    if (positioningType === 'shift' && rollLength && newElement.type === 'image') {
      // a duplicate on a rolling gang sheet takes the first free spot, like new artwork,
      // instead of landing on top of the original; the sheet grows to it
      const footprint = getElementFootprint(newElement);
      const spot = findSpotOnRollingSheet(
        state.design.designData?.spreads[destinationSpreadIndex],
        destinationContentArea,
        rollLength,
        { widthMm: footprint.width, heightMm: footprint.height },
        getResolvedGapMm(state) ?? state.hostSettings.gangSheet?.defaultGapMm ?? 0,
      );
      if (!spot) {
        dispatch(showSheetLengthLimitPopupOperation());
        return undefined;
      }
      newElement.x = spot.x + (newElement.x - footprint.left);
      newElement.y = spot.y + (newElement.y - footprint.top);
    } else if (positioningType === 'shift') {
      if (newElement.type === 'line') {
        newElement.x1 += 10;
        newElement.x2 += 10;
        newElement.y1 += 10;
        newElement.y2 += 10;
      } else {
        newElement.x += 10;
        newElement.y += 10;
      }
    } else if (positioningType === 'fit') {
      if (newElement.type === 'line') {
        newElement.x1 = destinationContentArea.left;
        newElement.x2 = destinationContentArea.left + destinationContentArea.width;
        newElement.y1 = destinationContentArea.top + destinationContentArea.height / 2;
        newElement.y2 = destinationContentArea.top + destinationContentArea.height / 2;
      } else {
        const scale = Math.min(
          destinationContentArea.width / newElement.width,
          destinationContentArea.height / newElement.height,
        );
        newElement.width *= scale;
        newElement.height *= scale;
        if (newElement.width > newElement.height) {
          newElement.x = destinationContentArea.left;
          newElement.y = destinationContentArea.top + (destinationContentArea.height - newElement.height) / 2;
        } else {
          newElement.x = destinationContentArea.left + (destinationContentArea.width - newElement.width) / 2;
          newElement.y = destinationContentArea.top;
        }

        if (newElement.type === 'image' || newElement.type === 'addon') {
          newElement.pw *= scale;
          newElement.ph *= scale;
        }

        if (newElement.type === 'text') {
          newElement.extra = {
            ...newElement.extra,
            fontSize: newElement.extra.fontSize * scale,
          };
        }
      }
    }

    batch(() => {
      dispatch(addMediaElementOperation(destinationSpreadIndex, destinationPageIndex, newElement));
      if (focus) {
        dispatch(addSelectedMediaElementOperation(newElement.uuid));
      }
    });

    return newElement;
  };

export default copyMediaElementOperation;
